import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Check, X, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import { adminListCategories, adminCreateCategory, adminUpdateCategory, adminDeleteCategory } from '../../api/categories';

const FALLBACK_SLUG = 'generale';
const errMsg = (e) => String(e.response?.data?.detail || '').split('|').pop() || 'Operazione non riuscita';

/**
 * AdminCategoriesPanel — Phase F5.2 "Categorie Persone".
 * Create / rename (slug follows the name and is cascaded) / order /
 * activate-deactivate / delete (references fall back to "Generale").
 */
const AdminCategoriesPanel = () => {
  const { toast } = useToast();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setCats(await adminListCategories()); } catch (_e) { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim() || busy) return;
    setBusy(true);
    try {
      await adminCreateCategory(newName.trim());
      setNewName('');
      toast({ title: 'Categoria creata' });
      load();
    } catch (e) {
      toast({ title: 'Errore', description: errMsg(e), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const patch = async (id, body, okMsg) => {
    try {
      const fresh = await adminUpdateCategory(id, body);
      if (okMsg) toast({ title: okMsg, description: body.name ? `Slug aggiornato: /${fresh.slug}` : undefined });
      load();
    } catch (e) {
      toast({ title: 'Errore', description: errMsg(e), variant: 'destructive' });
    }
  };

  const remove = async () => {
    if (!toDelete || busy) return;
    setBusy(true);
    try {
      const r = await adminDeleteCategory(toDelete.id);
      toast({ title: 'Categoria eliminata', description: `${r.moved_users} utenti, ${r.moved_posts} racconti e ${r.moved_lives} live spostati su "Generale".` });
      setToDelete(null);
      load();
    } catch (e) {
      toast({ title: 'Errore', description: errMsg(e), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center p-4"><Loader2 className="h-4 w-4 animate-spin" /></div>;

  return (
    <div className="space-y-2" data-testid="admin-categories-panel">
      <p className="text-[11px] text-muted-foreground">
        Una persona = UNA categoria. Rinominare aggiorna anche lo slug (/nome) e lo propaga a utenti, racconti e live.
        Disattivare nasconde la categoria da chips e selettore senza toccare gli utenti. Eliminare sposta tutto su "Generale".
      </p>
      <div className="flex gap-2">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Nuova categoria…"
          className="h-8 text-sm"
          data-testid="admin-cat-new-name"
        />
        <Button size="sm" onClick={create} disabled={busy || !newName.trim()} data-testid="admin-cat-create-btn">
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex flex-col gap-1.5">
        {cats.map((c) => (
          <div
            key={c.id}
            className={`flex items-center gap-2 rounded-md border border-border bg-background/40 px-2.5 py-1.5 ${c.active ? '' : 'opacity-60'}`}
            data-testid={`admin-cat-row-${c.slug}`}
          >
            <Input
              type="number"
              value={c.order}
              onChange={(e) => patch(c.id, { order: parseInt(e.target.value, 10) || 0 })}
              className="h-7 w-14 text-xs"
              data-testid={`admin-cat-order-${c.slug}`}
            />
            {editingId === c.id ? (
              <span className="flex min-w-0 flex-1 items-center gap-1">
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-7 text-sm" data-testid={`admin-cat-rename-input-${c.slug}`} />
                <button onClick={() => { patch(c.id, { name: editName.trim() }, 'Rinominata'); setEditingId(null); }} className="p-1 text-emerald-500" data-testid={`admin-cat-rename-save-${c.slug}`}>
                  <Check className="h-4 w-4" />
                </button>
                <button onClick={() => setEditingId(null)} className="p-1 text-muted-foreground"><X className="h-4 w-4" /></button>
              </span>
            ) : (
              <span className="flex min-w-0 flex-1 items-center gap-1.5">
                <span className="truncate text-sm font-semibold">{c.name}</span>
                <span className="text-[10px] text-muted-foreground" data-testid={`admin-cat-slug-${c.slug}`}>/{c.slug} · {c.users_count || 0} utenti</span>
                <button onClick={() => { setEditingId(c.id); setEditName(c.name); }} className="p-1 text-muted-foreground hover:text-foreground" data-testid={`admin-cat-rename-btn-${c.slug}`}>
                  <Pencil className="h-3 w-3" />
                </button>
              </span>
            )}
            <Switch
              checked={c.active}
              onCheckedChange={(v) => patch(c.id, { active: v }, v ? 'Attivata' : 'Disattivata')}
              data-testid={`admin-cat-active-${c.slug}`}
            />
            <button
              onClick={() => setToDelete(c)}
              disabled={c.slug === FALLBACK_SLUG}
              title={c.slug === FALLBACK_SLUG ? 'Categoria di fallback: non eliminabile' : 'Elimina categoria'}
              className="p-1 text-muted-foreground hover:text-destructive disabled:cursor-not-allowed disabled:opacity-30"
              data-testid={`admin-cat-delete-btn-${c.slug}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="admin-cat-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la categoria "{toDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              L'eliminazione è definitiva. {toDelete?.users_count || 0} {toDelete?.users_count === 1 ? 'utente verrà spostato' : 'utenti verranno spostati'} sulla
              categoria "Generale"; anche i racconti e le live collegati passeranno a "Generale".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-cat-delete-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={remove} disabled={busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="admin-cat-delete-confirm-btn">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCategoriesPanel;
